import { useState } from "react";
import { useTellescopeQuery } from "@tellescope/tanstack-query";
import { useVideoCalling } from "../store";

export type EnduserCallStage = "pre-connect" | "host-absent" | "host-left" | "in-call" | "disconnected";

/**
 * Decides what the enduser's call dialog shows. Once connected, the provider's presence in the Twilio room — not
 * the meeting's `live` status, which stays live after the provider leaves — decides between the call and the
 * waiting room, so an enduser never sits alone in an empty call view.
 */
export const useEnduserCallStage = (calendarEventId: string | null): EnduserCallStage => {
  const { isWaitingRoom, connectionState, participants } = useVideoCalling();
  const { data: event } = useTellescopeQuery({
    model: "calendar_events",
    method: "getOne",
    request: calendarEventId ?? "",
    enabled: !!calendarEventId,
  });

  // Participant identities are attendee ids; anyone who isn't an enduser attendee is staff. Without the event's
  // attendees another enduser in a group call could pass for the provider, so nobody counts until it loads.
  const isProviderPresent =
    !!event && participants.some((p) => !event.attendees.some((a) => a.type === "enduser" && a.id === p.identity));

  const [hasProviderBeenPresent, setHasProviderBeenPresent] = useState(false);
  if (isProviderPresent && !hasProviderBeenPresent) {
    setHasProviderBeenPresent(true);
  }

  if (isWaitingRoom) return "pre-connect";
  // Either the provider ended the meeting for everyone or the enduser's connection dropped
  if (connectionState !== "connected") return "disconnected";
  if (isProviderPresent) return "in-call";
  return hasProviderBeenPresent ? "host-left" : "host-absent";
};
